/**
 * Merge preferred-language solution with English fallback so empty AR/KU
 * collections (tabs, tab features, products, features) still render.
 */
export const normalizeSolutionLanguage = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const hasItems = (list) => Array.isArray(list) && list.length > 0;

const pickFilled = (preferred, fallback, keys) => {
  const out = { ...fallback, ...preferred };
  keys.forEach((key) => {
    const pref = preferred?.[key];
    const fall = fallback?.[key];
    if (typeof pref === "string" && !pref.trim() && typeof fall === "string" && fall.trim()) {
      out[key] = fall;
    }
  });
  return out;
};

const mergeTab = (preferredTab, fallbackTab) => {
  if (!preferredTab && !fallbackTab) return null;
  if (!preferredTab) return fallbackTab;
  if (!fallbackTab) return preferredTab;

  return {
    ...fallbackTab,
    ...preferredTab,
    label: String(preferredTab.label || fallbackTab.label || "").trim(),
    descriptionBody:
      String(preferredTab.descriptionBody || preferredTab.description || "").trim() ||
      fallbackTab.descriptionBody ||
      fallbackTab.description ||
      "",
    tabImageUrl: preferredTab.tabImageUrl || fallbackTab.tabImageUrl || "",
    tabImageUrls: hasItems(preferredTab.tabImageUrls)
      ? preferredTab.tabImageUrls
      : fallbackTab.tabImageUrls || [],
    features: hasItems(preferredTab.features)
      ? preferredTab.features
      : fallbackTab.features || [],
    displayOrder:
      typeof preferredTab.displayOrder === "number"
        ? preferredTab.displayOrder
        : fallbackTab.displayOrder,
  };
};

/**
 * @param {object|null} preferred - Solution for the active UI language
 * @param {object|null} english - English solution sibling (same solutionKey)
 */
export const mergeSolutionWithEnglishFallback = (preferred, english) => {
  if (!preferred && !english) return null;
  if (!preferred) return english;
  if (!english || preferred === english) return preferred;

  const preferredTabs = Array.isArray(preferred.descriptionTabs) ? preferred.descriptionTabs : [];
  const englishTabs = Array.isArray(english.descriptionTabs) ? english.descriptionTabs : [];

  let descriptionTabs;
  if (preferredTabs.length) {
    descriptionTabs = preferredTabs.map((tab, index) => {
      const enByOrder = englishTabs[index];
      const enByLabel = englishTabs.find(
        (t) =>
          String(t?.label || "").trim().toLowerCase() ===
          String(tab?.label || "").trim().toLowerCase()
      );
      return mergeTab(tab, enByLabel || enByOrder || null);
    });
  } else {
    descriptionTabs = englishTabs;
  }

  const merged = pickFilled(preferred, english, [
    "heroTag",
    "title",
    "introDescription",
    "cardDescription",
    "heroDescription",
    "specificationsLabel",
    "descriptionHeading",
    "descriptionBody",
    "productsTag",
    "productsTitle",
    "productsTitleAccent",
    "featuresTag",
    "featuresTitle",
    "featuresTitleAccent",
    "cardIconUrl",
    "heroImageUrl",
  ]);

  return {
    ...merged,
    descriptionTabs,
    features: hasItems(preferred.features) ? preferred.features : english.features || [],
    products: hasItems(preferred.products) ? preferred.products : english.products || [],
    pdfs: hasItems(preferred.pdfs) ? preferred.pdfs : english.pdfs || [],
  };
};
